import React, {useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Button, InputTextFormik} from '../../../../_gori/partials/widgets'
import AuthService from '../core/_requests'
import HttpStatusCode from '../../../api/HttpStatusCode'
import {toast} from 'react-toastify'

type TwoFactorMethod = {
  id: number
  method: 'email' | 'phone'
  config: {
    email?: string
    numbers?: string[]
    default?: string
  }
  enabled: boolean
  is_default: boolean
}

type Props = {
  show: boolean
  handleClose: () => void
  onVerifySuccess: (verifyResponse: any) => void
  data: any
}

const TwoFactorAuthModal: React.FC<Props> = ({show, handleClose, onVerifySuccess, data}) => {
  const [codeSentTo, setCodeSentTo] = useState<string>(
    data.default_method.method === 'email'
      ? data.default_method.config.email
      : data.default_method.config.default || ''
  )
  const [availableMethods] = useState<TwoFactorMethod[]>(data.available_methods || [])
  const [userToken] = useState<string>(data.user_token || '')
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>(data.default_method)
  const intl = useIntl()
  const initialOtpTime = 600
  const [message, setMessage] = useState<{
    otp: string | boolean
    responseSend2faCode: string | boolean
  }>({
    otp: false,
    responseSend2faCode: false,
  })
  const [rememberDevice, setRememberDevice] = useState<boolean>(false)
  const [view, setView] = useState<'verification' | 'selectMethod'>('verification')
  const [isResendingOTP, setIsResendingOTP] = useState(false)
  const [attemptsExceeded, setAttemptsExceeded] = useState(false)
  const [sendCodeCount, setSendCodeCount] = useState({
    send_code_count: 1,
    max_send: 3,
  })
  const [time, setTime] = useState<{
    otp: number
    resend: number
    verify: number
  }>({
    otp: initialOtpTime,
    resend: 60,
    verify: 0,
  })

  const validationSchema = Yup.object().shape({
    otp: Yup.array().of(
      Yup.string().matches(/^[0-9]$/, intl.formatMessage({id: 'OTP_MUST_BE_NUMBER'}))
    ),
  })

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [])

  const verifyOtp = useFormik({
    initialValues: {otp: ['', '', '', '', '', '']},
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, {setSubmitting}) => {
      const filledCount = values.otp.filter((item) => item !== '').length
      if (filledCount === 0) {
        setMessage((prev) => ({...prev, otp: intl.formatMessage({id: 'OTP_REQUIRED'})}))
        return
      } else if (filledCount < 6) {
        setMessage((prev) => ({...prev, otp: intl.formatMessage({id: 'OTP_6_DIGIT'})}))
        return
      }
      setMessage((prev) => ({...prev, responseSend2faCode: false}))
      setSubmitting(true)
      try {
        const verifyCode = values.otp.join('')
        const payload = {
          method: selectedMethod.method,
          user_token: userToken,
          verify_code: verifyCode,
          remember_device: rememberDevice,
        }

        const response = await AuthService.verify2FACode(payload)

        if (response.status) {
          onVerifySuccess(response)
        } else {
          if (Number(response.attempts_remaining) <= 0) {
            setMessage((prev) => ({
              ...prev,
              otp: intl.formatMessage({id: 'MAX_OTP_ATTEMPTS_REACHED'}),
              responseSend2faCode: false,
            }))
            setAttemptsExceeded(true)
            return
          }
          setMessage((prev) => ({
            ...prev,
            otp: intl.formatMessage(
              {id: 'VERIFY_FAILED'},
              {attempts_remaining: response.attempts_remaining || 0}
            ),
          }))
          verifyOtp.resetForm()
        }
      } catch (error: any) {
        const errorData = error?.response?.data?.error
        if (error?.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
          setMessage((prev) => ({
            ...prev,
            otp: errorData?.message,
            responseSend2faCode: false,
          }))
          return
        }

        if (errorData?.fields?.attempts_exceeded) {
          setMessage((prev) => ({
            ...prev,
            otp: intl.formatMessage({id: 'MAX_OTP_ATTEMPTS_REACHED'}),
            responseSend2faCode: false,
          }))
          setAttemptsExceeded(true)
        } else {
          const errorMessage =
            error?.response?.data?.error?.message || intl.formatMessage({id: 'VERIFY_FAILED'})
          setMessage((prev) => ({...prev, otp: errorMessage}))
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleSend2FACode = useCallback(
    async (method?: TwoFactorMethod) => {
      setIsResendingOTP(true)
      setAttemptsExceeded(false)
      setMessage((prev) => ({...prev, responseSend2faCode: false, otp: false}))
      const methodToUse = method || selectedMethod
      const payload = {
        method: methodToUse.method,
        user_token: userToken,
      }

      await AuthService.send2FACode(payload)
        .then((response) => {
          setTime((prev) => ({...prev, otp: initialOtpTime}))
          setMessage((prev) => ({
            ...prev,
            otp: false,
            responseSend2faCode: intl.formatMessage({id: 'MESSAGE_RESEND'}),
          }))
          setAttemptsExceeded(false)
          if (response.send_code_count && response.max_send) {
            setSendCodeCount({
              send_code_count: response.send_code_count,
              max_send: response.max_send,
            })
          }
          verifyOtp.resetForm()
        })
        .catch((error: any) => {
          if (error?.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
            const errorData = error?.response?.data?.error
            if (errorData?.fields?.max_send_reached) {
              setMessage((prev) => ({
                ...prev,
                otp: errorData?.message,
              }))
            }
          } else {
            let errorMessage = error?.response?.data?.error?.message
            toast.error(errorMessage)
          }
          setMessage((prev) => ({...prev, responseSend2faCode: false}))
        })
        .finally(() => {
          setIsResendingOTP(false)
        })
    },
    [intl, selectedMethod, userToken, verifyOtp]
  )

  const handlePasteOTP = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      const digits = pastedData.replace(/\D/g, '').slice(0, 6)

      if (digits.length === 6) {
        const newOtpValues = digits.split('')
        verifyOtp.setFieldValue('otp', newOtpValues)

        // Auto-submit after paste
        setTimeout(() => {
          verifyOtp.submitForm()
        }, 100)
      }
    },
    [verifyOtp]
  )

  const maskContact = useCallback((contact: string, visibleChars: number = 4) => {
    if (!contact) return ''

    // Check if it's an email
    if (contact.includes('@')) {
      const [localPart, domain] = contact.split('@')
      if (localPart.length <= visibleChars) {
        return `${localPart}@${domain}`
      }
      const visiblePart = localPart.slice(-visibleChars)
      return `***${visiblePart}@${domain}`
    }

    // It's a phone number
    const digits = contact.replace(/\D/g, '')
    if (digits.length <= visibleChars) return digits

    const visiblePart = digits.slice(-visibleChars)
    return `***-***-${visiblePart}`
  }, [])

  const ContactSupportLink = () => (
    <a
      href='/support'
      className='text-primary text-decoration-underline fw-bold'
      target='_blank'
      rel='noopener noreferrer'
    >
      {intl.formatMessage({id: 'CONTACT_SUPPORT'})}
    </a>
  )

  const ResendCodeButton = () => (
    <Button
      className='btn btn-advanced-primary'
      label={intl.formatMessage({id: 'RESEND_CODE'})}
      loadingText={intl.formatMessage({id: 'SENDING'})}
      event={() => handleSend2FACode(selectedMethod)}
      disabled={isResendingOTP}
      loading={isResendingOTP}
    />
  )

  const ResendCodeText = () => (
    <div className='text-center mb-3 justify-content-center d-flex'>
      <span className='me-2 text-black-50'>{intl.formatMessage({id: 'MESSAGE_RESEND_CODE'})}</span>
      <span
        className='text-danger cursor-pointer'
        onClick={() => {
          verifyOtp.resetForm()
          handleSend2FACode(selectedMethod)
        }}
      >
        {intl.formatMessage({id: 'RESEND_CODE'})}
      </span>
    </div>
  )

  const focusToNextOTP = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputName = event?.target?.name
      const match = inputName.match(/\[(\d+)]/)
      const index = match ? parseInt(match[1], 10) : null

      if (index === null) return

      // Check if all 6 OTP fields are filled after this input
      const currentValues = [...verifyOtp.values.otp]
      currentValues[index] = event.target.value

      const allFilled = currentValues.every((value) => value !== '')

      if (allFilled) {
        // Auto-submit when all 6 digits are entered
        setTimeout(() => {
          verifyOtp.submitForm()
        }, 100)
        return
      }

      // Otherwise, focus to next input if not the last one
      if (index < 5) {
        const nextOtpInput = document.querySelector<HTMLInputElement>(
          `input[name="otp[${index + 1}]"]`
        )
        if (nextOtpInput) {
          nextOtpInput.focus()
        }
      }
    },
    [verifyOtp]
  )

  useEffect(() => {
    let timer
    if (time.otp > 0) {
      timer = setInterval(() => {
        setTime((prev) => ({...prev, otp: prev.otp - 1}))
      }, 1000)
    } else if (time.otp === 0) {
      setMessage((prev) => ({...prev, otp: false, responseSend2faCode: false}))
    }
    return () => clearInterval(timer)
  }, [time.otp])

  return (
    <Modal
      id='gori_modal_2fa'
      tabIndex={-1}
      centered
      dialogClassName='modal-size-md h-auto'
      contentClassName='modal-padding'
      show={show}
      backdrop='static'
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        {view === 'selectMethod' && (
          <i
            className='bi bi-chevron-left fs-2 cursor-pointer me-3'
            onClick={() => setView('verification')}
          ></i>
        )}
        <Modal.Title bsPrefix={'fw-bolder fs-1'}>
          {intl.formatMessage({id: 'TWO_FACTOR_AUTHENTICATION'})}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='vh-75'>
        {view === 'verification' ? (
          <div>
            <h2 className='text-xl font-semibold mb-4 text-center'>
              {intl.formatMessage({id: 'VERIFY_CODE'})}
            </h2>
            <div>
              <p className='text-center'>
                {intl.formatMessage(
                  {id: 'ENTER_THE_CODE_SENT_TO'},
                  {contact: maskContact(codeSentTo)}
                )}
              </p>
            </div>
            <div className='justify-content-center d-flex'>
              <p className='mb-4 me-2'>{intl.formatMessage({id: 'REMAINING'})}</p>
              <span className='text-primary'>{formatTime(time.otp)}</span>
            </div>

            <div className='mt-4 mb-10 mx-10'>
              <div className='d-flex w-75 m-auto'>
                {Array.from({length: 6}).map((_, index) => (
                  <InputTextFormik
                    key={index}
                    type='text'
                    name={`otp[${index}]`}
                    maxlength={1}
                    formik={verifyOtp}
                    className='mx-1 otp'
                    onChange={(event: any) => focusToNextOTP(event)}
                    onPaste={handlePasteOTP}
                    disabled={time.otp === 0 || verifyOtp.isSubmitting || attemptsExceeded}
                  />
                ))}
              </div>
            </div>

            {/* Show expired message or other error messages */}
            {time.otp === 0 && !message.otp ? (
              <div className='text-danger text-center'>
                {intl.formatMessage({id: 'OTP_EXPIRED'})}
              </div>
            ) : (
              message.otp && <div className='text-danger text-center'>{message.otp}</div>
            )}

            {time.otp > 0 && !attemptsExceeded && !message.otp && <ResendCodeText />}

            {message.responseSend2faCode && (
              <div className='text-primary text-center'>{message.responseSend2faCode}</div>
            )}

            <div className='mt-7 d-flex justify-content-center'>
              <div className='form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='rememberDevice'
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='rememberDevice'>
                  {intl.formatMessage({id: 'REMEMBER_DEVICE_2_WEEKS'})}
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h2 className='text-xl font-semibold mb-4 text-center'>
              {intl.formatMessage({id: 'OTHER_WAYS_TO_LOG_IN'})}
            </h2>
            <div className='mt-8 mb-8 ps-8'>
              {availableMethods.map((method) => (
                <div
                  key={method.id}
                  className='d-flex light-hover justify-content-between align-items-center py-4 px-3 border-bottom cursor-pointer hover-bg-light'
                  onClick={async () => {
                    const newCodeSentTo =
                      method.method === 'email' ? method.config.email : method.config.default
                    setCodeSentTo(newCodeSentTo || '')
                    setSelectedMethod(method)
                    setView('verification')
                    if (method.method !== selectedMethod.method) {
                      await handleSend2FACode(method)
                    }
                  }}
                >
                  <div>
                    <div className='fw-bold text-dark'>
                      {method.method === 'email'
                        ? intl.formatMessage({id: 'VERIFY_BY_EMAIL'})
                        : intl.formatMessage({id: 'VERIFY_BY_PHONE'})}
                    </div>
                    <div className='text-muted'>
                      {method.method === 'email'
                        ? method.config.email
                        : maskContact(method.config.default || '')}
                    </div>
                  </div>
                  <i className='bi bi-chevron-right fs-4 text-gray-600'></i>
                </div>
              ))}
            </div>

            {availableMethods.length === 1 && (
              <div className='text-center text-muted d-flex justify-content-center'>
                {intl.formatMessage({id: 'NO_OTHER_2FA_METHODS'})}
                <a
                  href='/support'
                  className='ms-1 me-2 text-primary fw-bold text-decoration-underline'
                >
                  {intl.formatMessage({id: 'CONTACT_SUPPORT'})}
                </a>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {view === 'verification' && (
        <Modal.Footer>
          <div className='d-flex justify-content-between w-100'>
            <Button
              className='btn btn-link text-primary'
              label={intl.formatMessage({id: 'TRY_ANOTHER_WAY'})}
              event={() => setView('selectMethod')}
              disabled={verifyOtp.isSubmitting}
            />
            <div className='d-flex'>
              {time.otp === 0 ? (
                <>
                  <div className='d-flex justify-content-end'>
                    <ResendCodeButton />
                  </div>
                </>
              ) : attemptsExceeded ? (
                <>
                  {sendCodeCount.send_code_count < sendCodeCount.max_send ? (
                    <>
                      <div className='d-flex justify-content-end'>
                        <ResendCodeButton />
                      </div>
                    </>
                  ) : (
                    <div className='d-flex justify-content-center w-100'>
                      <ContactSupportLink />
                    </div>
                  )}
                </>
              ) : (
                /* Normal state: Show regular buttons */
                <>
                  <div className='d-flex justify-content-end'>
                    <Button
                      className='btn btn-advanced-secondary me-2'
                      label={intl.formatMessage({id: 'CLOSE'})}
                      loadingText={intl.formatMessage({id: 'CLOSE'})}
                      event={handleClose}
                      disabled={verifyOtp.isSubmitting || isResendingOTP}
                    >
                      {verifyOtp.isSubmitting ? (
                        <span className='spinner-border spinner-border-sm me-2' />
                      ) : null}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export {TwoFactorAuthModal}
